import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import {v4} from 'uuid';
import {t} from 'i18next';
import {GridSortModel} from '@mui/x-data-grid';
import {useLocation, useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  PAGE_SIZE,
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  getAppliedFilterValueObject,
  getReportIdFromLocation,
  getTagByCountTagReportIdPayload,
  mergeApiDataIntoConfig,
  tagColumns,
  TAGS_COUNT_REPORT_TYPE,
  updateApplyButtonState,
} from './Helper';
import StandardReports from '../../StandardReports';
import {
  useGetReportState,
  useLeftPanel,
  useLeftPanelReport,
  useReviewerCountExport,
  useTagCountReport,
  useTagCountView,
} from '../../../../../hooks/reporting/useReporting';
import {LeftPanelResponse} from '../../../../../shared/interfaces';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {TagCountReportItemType} from '../../../../../api/types/reportingApi.type';
import './TagCountsByTagReportContainer.scss';

dayjs.extend(utc);

interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}
const reportConfigFileName = 'TagcountsByTagReport';

const TagCountsByTagReportContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isQueryRun, setIsQueryRun] = useState(false);
  const [reportConfig, setReportConfig] = useState<any | null>(null);
  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>({
      pageSize: PAGE_SIZE,
      pageNumber: 1,
      sort: [],
    });
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });
  const [savedFilterValues, setSavedFilterValues] = useState<
    Record<string, any>
  >({});
  const [tagCountResultData, setTagCountResultData] = useState<
    TagCountReportItemType[]
  >([]);
  // Store reportid and notificationid for wait to complete modal when apply button clicked
  const [waitToCompleteReportData, setWaitToCompleteReportData] = useState<{
    reportId: number;
    notificationId: number;
  }>({reportId: 0, notificationId: 0});
  const initialJsonConfig = useRef<any | null>(null);
  const [dataAsOf, setDataAsOf] = useState<string>(() =>
    dayjs.utc().format('YYYY/MM/DD HH:mm:ss')
  );

  // To fetch left panel data
  const {
    data: leftPanelData,
    isLoading: isLoadingLeftPanelData,
    isError: isErrorLeftPanelData,
    error: leftPanelError,
    refetch: fetchLeftPanelData,
  } = useLeftPanel(4, false); // 4 is reportTypeCode for Tag by Count Report

  // To generate report id with left panel applied data
  const {
    isPending: isTagCountReportLoading,
    mutate: fetchTagCountReport,
    data: tagCountReportData,
  } = useTagCountReport();

  // To get report result data with report id
  const {isPending: isTagCountViewLoading, mutate: fetchTagCountView} =
    useTagCountView();

  // To Get Report state from reportId
  const {
    refetch: fetchReportState,
    isLoading: isReportStateLoading,
    data: reportStateData,
    error: reportStateError,
    isError: isReportStateError,
    isFetching: isFetchingReportState,
  } = useGetReportState(getReportIdFromLocation(location) ?? 0, false);

  // Export Report api
  const {
    mutate: reviewerCountsExport,
    isPending: isReviewerCountsExportLoading,
  } = useReviewerCountExport();

  // To get Saved Report Data
  const {
    data: savedReportData,
    isPending: isSavedReportLoading,
    error: savedReportError,
    isError: isSavedReportError,
    refetch: fetchSavedReportData,
  } = useLeftPanelReport(getReportIdFromLocation(location) ?? 0, false);

  const handleExportClick = () => {
    const payload = {
      ActionType: 'EXPORTREPORT',
      ReportType: TAGS_COUNT_REPORT_TYPE,
      SelectionCriteria: {
        ...getTagByCountTagReportIdPayload(
          savedFilterValues,
          leftPanelData as unknown as LeftPanelResponse
        ).tagcountReportInputs,
        dataAsOf: dayjs
          .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
          .toISOString(),
      },
    };

    reviewerCountsExport(payload, {
      onSuccess: () => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Success,
          message: t('reports.common.reportExportSuccess'),
        });
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t('reports.common.reportExportError'),
        });
      },
    });
  };

  const rightPanelHeaderConfig = useMemo<RightPanelConfig>(() => {
    return {
      subTitle: t('reports.common.dateAsOf', {
        date: dataAsOf,
      }),
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: false,
      onUploadClick: handleExportClick,
    };
  }, [t, savedFilterValues, leftPanelData, dataAsOf]);

  const payloadForSaveReport = useMemo(() => {
    if (savedFilterValues)
      return {
        reportId: getReportIdFromLocation(location) ?? 0,
        reportTypeCode: TAGS_COUNT_REPORT_TYPE,
        ...getTagByCountTagReportIdPayload(
          savedFilterValues,
          leftPanelData as unknown as LeftPanelResponse
        ).tagcountReportInputs,
      };
  }, [savedFilterValues, leftPanelData, location]);

  // Observe location change for report fetch
  useEffect(() => {
    const reportId = getReportIdFromLocation(location);
    if (leftPanelData && !isLoadingLeftPanelData && !isErrorLeftPanelData) {
      if (reportId && reportId > 0) {
        if (location.state?.isBackgroundReport) {
          // If navigated from background report, reset the report data and fetch result
          setReportConfig(null);
          setTagCountResultData([]);
          setIsQueryRun(false);
          setWaitToCompleteReportData({reportId: 0, notificationId: 0});
          fetchReportState();
        } else if (location.state?.fromReportLanding) {
          // Saved report open case handle
          fetchSavedReportData();
        }
      }
    } else {
      fetchLeftPanelData();
    }
  }, [location]);

  // Left side panel data response handling
  useEffect(() => {
    if (leftPanelData && !isLoadingLeftPanelData && !isErrorLeftPanelData) {
      loadReportConfig().then((data: Reports | null) => {
        if (location.state?.isBackgroundReport && data) {
          fetchReportState();
          return;
        }

        if (
          location.state?.fromReportLanding &&
          data &&
          getReportIdFromLocation(location) > 0
        ) {
          fetchSavedReportData();
          return;
        }

        if (data)
          setReportConfig(
            mergeApiDataIntoConfig(
              data,
              leftPanelData as unknown as LeftPanelResponse
            )
          );
      });
    } else if (
      !isLoadingLeftPanelData &&
      !isErrorLeftPanelData &&
      leftPanelError
    ) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (leftPanelError as any)?.response?.data?.title ??
          t('reports.common.reportConfigAPIError'),
      });
    }
  }, [isLoadingLeftPanelData, isErrorLeftPanelData, leftPanelError]);

  // Report state data handling for background notification open case
  useEffect(() => {
    if (
      !isReportStateLoading &&
      !isReportStateError &&
      reportStateData &&
      !isFetchingReportState
    ) {
      setIsQueryRun(true);
      setTagCountResultData(
        (reportStateData?.tagcountReportDetails ?? []).map((item) => ({
          ...item,
          id: v4(),
          tagname: `${item.tagname}${_.get(item, 'tagClassificationName', '') !== '' ? ` (${item.tagClassificationName})` : ''}`,
        }))
      );
      const updateConfig = mergeApiDataIntoConfig(
        initialJsonConfig.current,
        leftPanelData as unknown as LeftPanelResponse,
        reportStateData?.progressReportFilteration
      );
      setReportConfig(updateConfig);
      setSavedFilterValues(getAppliedFilterValueObject(updateConfig));
    } else if (
      !isReportStateLoading &&
      isReportStateError &&
      reportStateError &&
      !isFetchingReportState
    ) {
      // show error toast
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (reportStateError as any)?.response?.data?.title ??
          t('reports.common.reportAPIError'),
      });
    }
  }, [
    isReportStateLoading,
    isReportStateError,
    reportStateError,
    isFetchingReportState,
  ]);

  // Saved Report data handling
  useEffect(() => {
    if (
      !isSavedReportLoading &&
      !isSavedReportError &&
      savedReportData &&
      location.state?.fromReportLanding
    ) {
      // Merge saved report data into left panel and config
      const updatedConfig = mergeApiDataIntoConfig(
        initialJsonConfig.current,
        leftPanelData as unknown as LeftPanelResponse,
        savedReportData
      );
      setReportConfig(updatedConfig);
    } else if (
      !isSavedReportLoading &&
      isSavedReportError &&
      savedReportError
    ) {
      // show error toast
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          savedReportError?.response?.data?.title ??
          t('reports.common.reportAPIError'),
      });
    }
  }, [isSavedReportLoading, isSavedReportError, savedReportData]);

  const gridData = useMemo(() => {
    let data = tagCountResultData;
    // Apply sorting
    if (paginationSortConfig.sort && paginationSortConfig.sort.length > 0) {
      const sortItem = paginationSortConfig.sort[0];
      data = _.orderBy(
        data,
        [sortItem.field],
        [sortItem.sort === 'asc' ? 'asc' : 'desc']
      );
    } else {
      data = _.orderBy(data, ['tagName'], ['asc']);
    }
    // Apply pagination
    const startIndex =
      (paginationSortConfig.pageNumber - 1) * paginationSortConfig.pageSize;
    const endIndex = startIndex + paginationSortConfig.pageSize;
    return data.slice(startIndex, endIndex);
  }, [tagCountResultData, paginationSortConfig]);

  const handleFilterChange = useCallback((values: Record<string, any>) => {
    setReportConfig((prevReportConfig: Reports | null) => {
      if (!prevReportConfig) return prevReportConfig;
      return updateApplyButtonState(prevReportConfig, values);
    });
  }, []);

  const handleActionClick = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));
      setSavedFilterValues(values);
      setIsQueryRun(false);
      setTagCountResultData([]);
      setWaitToCompleteReportData({reportId: 0, notificationId: 0});
      const payload = getTagByCountTagReportIdPayload(
        values,
        leftPanelData as unknown as LeftPanelResponse
      );
      fetchTagCountReport(payload, {
        onSuccess: (data) => {
          setWaitToCompleteReportData({
            reportId: data.reportID,
            notificationId: data.notificationID,
          });
        },
        onError: (error: any) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              error?.response?.data?.title ??
              t('reports.common.runReportFailedMsg'),
          });
        },
      });
    } else if (actionKey === 'reset') {
      if (reportConfig && leftPanelData) {
        setReportConfig(
          mergeApiDataIntoConfig(
            reportConfig,
            leftPanelData as unknown as LeftPanelResponse
          )
        );
        setIsQueryRun(false);
        setSavedFilterValues({});
        setWaitToCompleteReportData({reportId: 0, notificationId: 0});
        setTagCountResultData([]);

        if (getReportIdFromLocation(location) > 0) {
          navigate(location.pathname, {
            replace: true,
            state: {
              isBackgroundReport: false,
            },
          });
        }
      }
    }
  };

  const loadReportConfig = useCallback(async (): Promise<Reports | null> => {
    try {
      const data: Reports = await import(
        `../../config/${reportConfigFileName}.json`
      );
      initialJsonConfig.current = data;
      return data;
    } catch (error) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t('reports.common.reportConfigAPIError'),
      });
      return Promise.reject(error);
    }
  }, []);

  if (_.isNil(leftPanelData) || _.isNil(reportConfig) || isLoadingLeftPanelData)
    return <ArrLoadingSpinner isOpen overlay />;

  return (
    <>
      <StandardReports
        className="tag-counts-by-tag-report-container"
        title={
          location?.state?.fromReportLanding
            ? (location?.state?.item?.label ?? '')
            : (reportConfig?.title ?? '')
        }
        infoContent={reportConfig?.infoContent}
        infoModalTitle={reportConfig?.infoModalTitle ?? ''}
        showInfoIcon={reportConfig?.showInfoIcon ?? false}
        reportsConfig={reportConfig?.config as any}
        data={gridData}
        mapRows={(data) => data}
        columns={tagColumns}
        selectedOption={null}
        observReportConfigUpdate={false}
        isQueryRun={isQueryRun}
        enableSave={isQueryRun}
        onFilterChange={handleFilterChange}
        onAction={handleActionClick}
        validateActionFromParent
        loading={isTagCountViewLoading}
        getRowId={(row) => row.id}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        saveReportConfig={payloadForSaveReport}
        gridConfig={{
          primaryKey: 'id',
          className: 'tag-report-grid',
          totalRecords: tagCountResultData.length,
          disableVirtualization: false,
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
          disableColumnResize: false,
          itemsPerPage: paginationSortConfig.pageSize,
          currentPage: paginationSortConfig.pageNumber,
          setItemsPerPage: (pageSize: number) =>
            setPaginationConfig((prev) => ({
              ...prev,
              pageSize,
              pageNumber: 1,
            })),
          onPageChange: (pageNumber: number) => {
            setPaginationConfig((prev) => ({...prev, pageNumber}));
          },
          sortModel: paginationSortConfig.sort,
          onSortModelChange: (sortModel: GridSortModel) =>
            setPaginationConfig((prev) => ({
              ...prev,
              sort: sortModel,
              pageNumber: 1,
            })),
          getRowHeight: () => 'auto',
          entityType: t('common.entries'),
        }}
      />
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      {(isTagCountReportLoading || isReviewerCountsExportLoading) && (
        <ArrLoadingSpinner isOpen overlay />
      )}
      {_.get(waitToCompleteReportData, 'notificationId', 0) !== 0 &&
        _.get(waitToCompleteReportData, 'reportId', 0) !== 0 && (
          <ArrWaitToCompleteModalWrapper
            showClose={false}
            reportId={_.get(waitToCompleteReportData, 'reportId', 0)}
            notificationId={_.get(
              waitToCompleteReportData,
              'notificationId',
              0
            )}
            onComplete={(reportId) => {
              if (tagCountReportData?.reportID === reportId) {
                // Report generation completed
                fetchTagCountView(
                  {
                    ReportID: tagCountReportData.reportID,
                    ReportType: TAGS_COUNT_REPORT_TYPE,
                    ActionType: tagCountReportData.actionType,
                    ReportFilePath: tagCountReportData.reportFilePath,
                    ReportName: tagCountReportData.reportName,
                  },
                  {
                    onSuccess: (data) => {
                      setIsQueryRun(true);
                      setTagCountResultData(
                        data.tagcountReportDetails.map((item) => ({
                          ...item,
                          id: v4(),
                          tagname: `${item.tagname}${_.get(item, 'tagClassificationName', '') !== '' ? ` (${item.tagClassificationName})` : ''}`,
                        }))
                      );
                    },
                    onError: (error: any) => {
                      setToastMsgConfig({
                        show: true,
                        type: ToastType.Error,
                        message:
                          error?.response?.data?.title ??
                          t('reports.common.reportAPIError'),
                      });
                    },
                  }
                );
              }
            }}
          />
        )}
    </>
  );
};

export default TagCountsByTagReportContainer;
